import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { renewFile } from '../api/data-connectors/renew-file'
import type { FileInfoType } from '../types'
import { formatDateWithYear } from '../utils/dateFormat'
import { useNotification } from './useNotification'

export function useRenewFile() {
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const { t, i18n } = useTranslation()

  return useMutation({
    mutationFn: ({ fileId }: { fileId: string }) => renewFile(fileId),
    // Snapshot the cache so a failed renewal can be undone without a refetch
    onMutate: async ({ fileId }) => {
      await queryClient.cancelQueries({ queryKey: ['filepool'] })
      const previousFilePool = queryClient.getQueryData<FileInfoType[]>(['filepool'])

      queryClient.setQueryData<FileInfoType[]>(['filepool'], (old) => {
        if (!old) return old
        return old.map((file) =>
          file.id === fileId
            ? {
                ...file,
                uploadedAt: new Date().toISOString(),
                expiresAt: new Date(Date.now() + 31 * 24 * 60 * 60 * 1000).toISOString(),
              }
            : file
        )
      })

      return { previousFilePool }
    },
    onSuccess: (data, { fileId }) => {
      // TODO: Potentially can just invalidate, needs testing
      queryClient.setQueryData<FileInfoType[]>(['filepool'], (old) => {
        if (!old) return old
        return old.map((file) =>
          file.id === fileId
            ? { ...file, uploadedAt: data.uploadedAt, expiresAt: data.expiresAt }
            : file
        )
      })

      notify({
        isSuccess: true,
        text: t('files.renewSuccessToast', {
          date: formatDateWithYear(data.expiresAt, i18n.language),
        }),
        position: 'bottom',
      })
    },
    onError: (_error, _variables, context) => {
      if (context?.previousFilePool) {
        queryClient.setQueryData(['filepool'], context.previousFilePool)
      }

      notify({ isSuccess: false, text: t('files.renewFailureToast'), position: 'bottom' })
    },
  })
}
